import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { Container } from "../components/Container";
import { SEO } from "../components/SEO";
import {
  downloadGuestEsimReceipt,
  getGuestEsimOrder,
  type GuestEsimOrderStatus,
} from "../lib/api";
import { useLocale } from "../hooks/useLocale";

type StoredCheckout = {
  orderId: string;
  email: string;
  packageId?: string;
};

function PaymentSuccessBadge({ visible }: { visible: boolean }) {
  const { t } = useTranslation();
  if (!visible) return null;

  return (
    <div
      className="esim-success-hero mt-10 mb-8 flex flex-col items-center text-center"
      role="status"
      aria-live="polite"
    >
      <div className="esim-success-burst" aria-hidden="true">
        <span className="esim-success-ring" />
        <span className="esim-success-ring esim-success-ring--delay" />
        <span className="esim-success-particle esim-success-particle--1" />
        <span className="esim-success-particle esim-success-particle--2" />
        <span className="esim-success-particle esim-success-particle--3" />
        <span className="esim-success-particle esim-success-particle--4" />
        <span className="esim-success-particle esim-success-particle--5" />
        <span className="esim-success-particle esim-success-particle--6" />
        <div className="esim-success-check">
          <svg viewBox="0 0 52 52" className="h-14 w-14" aria-hidden="true">
            <circle
              className="esim-success-check-circle"
              cx="26"
              cy="26"
              r="24"
              fill="none"
            />
            <path
              className="esim-success-check-mark"
              fill="none"
              d="M14.1 27.2l7.1 7.2 16.7-16.8"
            />
          </svg>
        </div>
      </div>
      <p className="esim-success-label mt-6 text-lg font-semibold text-success sm:text-xl">
        {t("esim.payment_confirmed")}
      </p>
      <p className="esim-success-sub mt-1 max-w-md text-sm text-text-muted sm:text-base">
        {t("esim.payment_confirmed_sub")}
      </p>
    </div>
  );
}

const PROVISIONING_TIP_KEYS = [
  "esim.provisioning_tip_1",
  "esim.provisioning_tip_2",
  "esim.provisioning_tip_3",
  "esim.provisioning_tip_4",
] as const;

function EsimProvisioningSkeleton({ confirmingPayment }: { confirmingPayment: boolean }) {
  const { t } = useTranslation();
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setTipIndex((i) => (i + 1) % PROVISIONING_TIP_KEYS.length);
    }, 3800);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div
      className="esim-provision-skeleton mt-8 overflow-hidden rounded-2xl border border-border-soft bg-bg-base p-5 shadow-sm dark:bg-bg-subtle"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-500">
            {confirmingPayment
              ? t("esim.awaiting_payment")
              : t("esim.provisioning_title")}
          </p>
          <p className="mt-2 text-sm text-text-muted">
            {confirmingPayment
              ? t("esim.provisioning_confirm_hint")
              : t("esim.provisioning")}
          </p>
        </div>
        <span className="esim-provision-orb" aria-hidden="true" />
      </div>

      <div className="esim-provision-progress mt-5" aria-hidden="true">
        <span className="esim-provision-progress-bar" />
      </div>

      <div className="mt-8 grid gap-8 sm:grid-cols-[auto_1fr] sm:items-start">
        <div className="mx-auto sm:mx-0">
          <div className="esim-skeleton-qr" aria-hidden="true">
            <div className="esim-skeleton-qr-grid">
              {Array.from({ length: 36 }).map((_, i) => (
                <span key={i} style={{ animationDelay: `${(i % 6) * 0.08}s` }} />
              ))}
            </div>
          </div>
          <p className="mt-3 text-center text-xs text-text-subtle">
            {t("esim.provisioning_qr_placeholder")}
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-3" aria-hidden="true">
            <div className="esim-skeleton-line w-2/3" />
            <div className="esim-skeleton-line w-full" />
            <div className="esim-skeleton-line w-5/6" />
            <div className="esim-skeleton-line w-4/5" />
            <div className="esim-skeleton-line mt-4 h-10 w-full rounded-xl" />
          </div>

          <p
            key={tipIndex}
            className="esim-provision-tip rounded-xl bg-bg-muted px-4 py-3 text-sm text-text-heading dark:bg-bg-base"
          >
            <span className="font-semibold text-brand-500">
              {t("esim.provisioning_tip_label")}
            </span>{" "}
            {t(PROVISIONING_TIP_KEYS[tipIndex])}
          </p>
        </div>
      </div>
    </div>
  );
}

export function EsimSuccess() {
  const { t } = useTranslation();
  const { pathFor } = useLocale();
  const [params] = useSearchParams();
  const [order, setOrder] = useState<GuestEsimOrderStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(true);
  const [celebrated, setCelebrated] = useState(false);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  const orderIdParam = params.get("orderId") ?? "";
  const emailParam = params.get("email") ?? "";

  const resolved = useMemo(() => {
    let stored: StoredCheckout | null = null;
    try {
      const raw = sessionStorage.getItem("albavox.guestEsim");
      if (raw) stored = JSON.parse(raw) as StoredCheckout;
    } catch {
      /* ignore */
    }
    return {
      orderId: orderIdParam || stored?.orderId || "",
      email: emailParam || stored?.email || "",
    };
  }, [orderIdParam, emailParam]);

  const isRefunded = order?.status === "Refunded";
  const isFailed =
    order?.status === "Failed" || order?.status === "Cancelled";
  const paymentOk =
    order?.status === "Fulfilled" ||
    order?.status === "Captured" ||
    order?.status === "Fulfilling";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Celebrate as soon as user lands from Stripe success URL (proud vibe),
  // then keep it if fulfillment succeeds. Hide if refunded/failed.
  useEffect(() => {
    if (resolved.orderId && resolved.email) {
      setCelebrated(true);
    }
  }, [resolved.orderId, resolved.email]);

  useEffect(() => {
    if (isRefunded || isFailed) {
      setCelebrated(false);
    } else if (paymentOk) {
      setCelebrated(true);
    }
  }, [paymentOk, isRefunded, isFailed]);

  useEffect(() => {
    const { orderId: resolvedOrderId, email: resolvedEmail } = resolved;

    if (!resolvedOrderId || !resolvedEmail) {
      setError(t("esim.missing_order"));
      setPolling(false);
      return;
    }

    let cancelled = false;
    let attempts = 0;

    async function poll() {
      attempts += 1;
      try {
        const data = await getGuestEsimOrder(resolvedOrderId, resolvedEmail);
        if (cancelled) return;
        setOrder(data);
        setError(null);

        const failed =
          data.status === "Refunded" ||
          data.status === "Failed" ||
          data.status === "Cancelled";

        const ready =
          data.status === "Fulfilled" &&
          (data.install?.qrCodeUrl || data.install?.lpaCode);

        // Keep polling while Fulfilled but QR/LPA still provisioning.
        if (ready || failed || attempts >= 40) {
          setPolling(false);
          return;
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : t("esim.load_order_error"),
          );
        }
      }

      if (!cancelled && attempts < 40) {
        window.setTimeout(poll, attempts < 5 ? 1500 : 3000);
      } else if (!cancelled) {
        setPolling(false);
      }
    }

    void poll();
    return () => {
      cancelled = true;
    };
  }, [resolved, t]);

  async function onDownloadReceipt() {
    if (!resolved.orderId || !resolved.email || receiptBusy) return;
    setReceiptBusy(true);
    setReceiptError(null);
    try {
      await downloadGuestEsimReceipt(resolved.orderId, resolved.email);
    } catch (e) {
      setReceiptError(
        e instanceof Error ? e.message : t("esim.receipt_download_error"),
      );
    } finally {
      setReceiptBusy(false);
    }
  }

  const install = order?.install;
  const canDownloadReceipt = Boolean(order?.receiptAvailable);
  const showInstall = Boolean(install?.qrCodeUrl || install?.lpaCode);

  return (
    <main className="min-h-screen bg-bg-base pt-28 sm:pt-32">
      <SEO
        title={t("esim.meta_title_success")}
        description={t("esim.meta_description_success")}
        path="/esim/success"
      />

      <Container size="md">
        <article className="py-12 sm:py-16">
          {isRefunded ? (
            <div
              className="mb-10 rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-900/50 dark:bg-red-950/40"
              role="alert"
            >
              <p className="text-lg font-semibold text-red-700 dark:text-red-300">
                {t("esim.refunded_title")}
              </p>
              <p className="mt-2 text-sm text-red-700/90 dark:text-red-200/90">
                {t("esim.refunded_body")}
              </p>
              {import.meta.env.DEV && order?.lastError ? (
                <p className="mt-3 break-words text-xs text-red-600/80 dark:text-red-300/80">
                  {order.lastError}
                </p>
              ) : null}
            </div>
          ) : null}

          <header className="max-w-2xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-brand-500">
              {t("esim.success_eyebrow")}
            </p>
            <h1 className="hero-headline text-text-heading">
              {isRefunded
                ? t("esim.refunded_headline")
                : t("esim.success_title")}
            </h1>
            <p className="mt-4 text-lg text-text-muted">
              {isRefunded
                ? t("esim.refunded_subtitle")
                : t("esim.success_subtitle")}
            </p>
          </header>

          <PaymentSuccessBadge visible={celebrated && !isRefunded && !isFailed} />

          <div className="mt-8 rounded-2xl border border-border-soft bg-bg-base p-5 shadow-sm dark:bg-bg-subtle">
            {order ? (
              <div className="space-y-2 text-sm text-text-heading">
                <p>
                  <span className="text-text-muted">{t("esim.order_label")}</span>{" "}
                  <code className="text-text-heading">{order.orderId}</code>
                </p>
                <p>
                  <span className="text-text-muted">{t("esim.status_label")}</span>{" "}
                  <strong
                    className={
                      isRefunded || isFailed
                        ? "text-red-600 dark:text-red-300"
                        : paymentOk
                          ? "text-success"
                          : "text-text-heading"
                    }
                  >
                    {order.status}
                  </strong>
                  {polling ? t("esim.refreshing") : null}
                </p>
                <p>
                  <span className="text-text-muted">{t("esim.package_label")}</span>{" "}
                  {order.packageId}
                </p>
                {!paymentOk && !isRefunded && !isFailed && polling ? (
                  <p className="pt-1 text-text-muted">{t("esim.awaiting_payment")}</p>
                ) : null}

                {canDownloadReceipt ? (
                  <div className="pt-4">
                    <button
                      type="button"
                      onClick={() => void onDownloadReceipt()}
                      disabled={receiptBusy}
                      className="inline-flex items-center gap-2 rounded-xl bg-success px-4 py-2.5 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-wait disabled:opacity-70"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 3v12m0 0l4-4m-4 4l-4-4M4 19h16"
                        />
                      </svg>
                      {receiptBusy
                        ? t("esim.receipt_downloading")
                        : t("esim.download_receipt")}
                    </button>
                    {receiptError ? (
                      <p className="mt-2 text-xs text-red-600 dark:text-red-300">
                        {receiptError}
                      </p>
                    ) : (
                      <p className="mt-2 text-xs text-text-muted">
                        {t("esim.receipt_hint")}
                      </p>
                    )}
                  </div>
                ) : paymentOk ? (
                  <p className="pt-3 text-xs text-text-muted">
                    {t("esim.receipt_preparing")}
                  </p>
                ) : null}
              </div>
            ) : error ? (
              <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
            ) : (
              <p className="text-sm text-text-muted">{t("esim.loading_order")}</p>
            )}
          </div>

          {showInstall ? (
            <div className="esim-install-reveal mt-8 grid gap-6 rounded-2xl border border-border-soft bg-bg-base p-5 shadow-sm dark:bg-bg-subtle">
              <h2 className="text-lg font-semibold text-text-heading">
                {t("esim.install_title")}
              </h2>
              {install?.qrCodeUrl ? (
                <img
                  src={install.qrCodeUrl}
                  alt={t("esim.qr_alt")}
                  className="mx-auto h-56 w-56 rounded-xl border border-border-soft bg-white object-contain p-2"
                />
              ) : null}
              <ol className="list-decimal space-y-2 pl-5 text-sm text-text-muted">
                <li>{t("esim.install_step1")}</li>
                <li>{t("esim.install_step2")}</li>
                <li>{t("esim.install_step3")}</li>
              </ol>
              {install?.lpaCode ? (
                <p className="break-all rounded-xl bg-bg-muted p-3 text-xs text-text-heading dark:bg-bg-base">
                  <strong>{t("esim.lpa_label")}</strong> {install.lpaCode}
                </p>
              ) : null}
              {install?.iosQuickInstall ? (
                <a
                  href={install.iosQuickInstall}
                  className="text-sm font-semibold text-brand-500 underline"
                >
                  {t("esim.ios_quick_install")}
                </a>
              ) : null}
              {install?.iccid ? (
                <p className="text-xs text-text-muted">
                  {t("esim.iccid_label", { iccid: install.iccid })}
                </p>
              ) : null}
            </div>
          ) : !isRefunded && !isFailed && (polling || !order) ? (
            <EsimProvisioningSkeleton confirmingPayment={!paymentOk} />
          ) : !isRefunded && !isFailed ? (
            <p className="mt-8 text-sm text-text-muted">
              {t("esim.install_not_ready")}
            </p>
          ) : null}

          <div className="mt-10 flex flex-wrap gap-4 text-sm">
            <Link to={pathFor("/esim/shop")} className="font-semibold text-brand-500">
              {t("esim.buy_another")}
            </Link>
            <Link to="/esim" className="font-semibold text-text-muted">
              {t("esim.open_in_app_short")}
            </Link>
          </div>
        </article>
      </Container>
    </main>
  );
}
